import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';

import { User, UserLoginForm } from '../models';

import { 
    _CLEAR,
    CLEAR_TASKS, 
    SET_MAIN_VIEW 
} from '../../ngrx/actions';

import { CredentialService } from './credential.service';
import { MQTTService } from './mqtt.service';

import { allUserFields } from '../../ngrx/graphql';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class AuthenticationService {
    userChangesDetected: Subject<any> = new Subject();
    logoutTrigger      : Subject<any> = new Subject();
    user;

    constructor(
        private credentialService: CredentialService,
        private mqttService      : MQTTService,
        private router           : Router,
        private store            : Store<any>
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser'));
        if (!this.user) {
            const navigationPromise = new Promise((resolve) => {
                this.router.events.subscribe((e) => {
                    if (e instanceof NavigationEnd) {
                        resolve(e.url);
                    }
                });
            });
          
            navigationPromise.then((currentURL: any) => {
                if (currentURL.indexOf('/public/rfid/') > -1) {
                    console.log('Public route is called')
                    return;
                }

                this.logout();
            });
        }
    }
    private invokeUsers(payload: any): Observable<any> {
        return new Observable(observer => {

            this.credentialService.lambda.invoke({
                FunctionName: environment.authUsersFunction,
                Payload: JSON.stringify(payload)
                }, (err, data) => {
                    if (err) {
                        if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
                            this.logout();
                        }
                    } else {
                        const output = typeof data.Payload === 'string'
                            ? JSON.parse(data.Payload)
                            : data.Payload;
                        observer.next(output);
                    }
                });
        });
    }

    private invokeUserMutation(payload: any): void {
        this.credentialService.lambda.invoke({
            FunctionName: environment.authUsersFunction,
            Payload: JSON.stringify(payload)
            }, (err, data) => {
                if (err) {
                    if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
                        this.logout();
                    }
                }
            }
        );
    }

    private invokeUsersSettingMutation(payload: any): void {
        this.credentialService.lambda.invoke({
            FunctionName: environment.authUsersSettingUpdateFunction,
            Payload: JSON.stringify(payload)
            }, (err, data) => {
                if (err) {
                    if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
                        this.logout();
                    }
                }
            }
        );
    }

    changePassword (email: string, oldPassword: string, newPassword: string) {
        return new Observable(observer => {

            this.credentialService.lambda.invoke({
                FunctionName: environment.changePasswordFunction,
                Payload: JSON.stringify({ 'email': email, 'oldPassword': oldPassword, 'newPassword': newPassword })
                }, (err, data) => {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        const output = typeof data.Payload === 'string'
                            ? JSON.parse(data.Payload)
                            : data.Payload;
                        observer.next(output);
                    }
                }
            );
        });
    }

    deleteUser(email: string): void {
        const variables = { email };
        const query = `mutation ($email: String!) {
            deleteUser(email: $email) {
                email
            }
        }`;

        this.invokeUserMutation({ query, variables });
    }

    getCurrentUser(): string | null {
        const currentUser = localStorage.getItem('currentUser');
        return !currentUser ? null : JSON.parse(currentUser)['email'];
    }

    getCurrentProcess(): Observable<any> {
        return this.store
            .select('main', 'mainView')
            .map(({ process }) => process);
    }

    getMainView(): Observable<any> {
        return this.store.select('main', 'mainView');
    }

    getUser(email: string): Observable<any> {
        const query = `{
            user(email: "${email}") {
                ${allUserFields}
            }
        }`;

        return this.invokeUsers({ query });
    }

    getUsers(query?: string): Observable<any> {
        const returnAllFields = `{
            users(role: "all") {
                ${allUserFields}
            }
        }`;

        return this.invokeUsers({ query: !query ? returnAllFields : query });
    }

    getUserList(): Observable<User[]> {
        return this.store.select('users', 'userList');
    }

    getUserRole(): string | null {
        const currentUser = localStorage.getItem('currentUser');
        return !currentUser ? null : JSON.parse(currentUser)['role'];
    }

    getUserTeam(): string | null {
        const currentUser = localStorage.getItem('currentUser');
        return !currentUser ? null : JSON.parse(currentUser)['team'];
    }

    login(email: string, password: string) {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('mqttRetry');
        
        return new Observable(observer => {

            this.credentialService.lambda.invoke({
                FunctionName: environment.loginFunction,
                Payload: JSON.stringify({ email: email, password: password})
                }, (err, data) => {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        const output = typeof data.Payload === 'string'
                            ? JSON.parse(data.Payload)
                            : data.Payload;

                        console.log(output);

                        if (!output.login) {
                            console.log('Not Logged in !');
                            observer.error(output.message);
                        } else {
                            console.log('Logged In');
                            output['email'] = email;
                            localStorage.setItem('currentUser', JSON.stringify(output));
                            localStorage.setItem('mqttRetry', '1');

                            this.credentialService.setCredentials(output);
                            this.credentialService.subscribeToMessages(0,5);

                            observer.next('Logged In');
                        }

                    }
                }
            );
        });
    }

    loginCheckRole(email: string, password: string) {
        return new Observable(observer => {
            this.credentialService.lambda.invoke({
                FunctionName: environment.checkRoleFunction,
                Payload: JSON.stringify({ email: email, password: password})
                }, (err, data) => {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        const output = typeof data.Payload === 'string'
                            ? JSON.parse(data.Payload)
                            : data.Payload;

                        if (!output) {
                            console.log('Error Check Role Function.');
                            observer.error(output);
                        } else {                            
                            if(output.isAdmin){
                                console.log('ADMIN Logged In');
                            }else{
                                console.log('ADMIN Logged fail');
                            }
                            observer.next(output);
                        }

                    }
                }
            );
        });
    }
    
    logout() {
        this.store.dispatch({
            type: SET_MAIN_VIEW,
            payload: { loggingOut: true }
        });

        setTimeout(() => {
            localStorage.removeItem('currentUser');
            localStorage.removeItem('mqttRetry');
            this.clearHorizonConfig();
            this.router.navigate(['/pages/login']);
            this.logoutTrigger.next();
            this.mqttService.disconnectIoT("mqtt disconnect from authService.logout");
            this.credentialService.clearCredentials();

            this.store.dispatch({ type: CLEAR_TASKS });
            this.store.dispatch({ type: _CLEAR });
        }, 1000);
    }

    lostPassword(email: string) {
        return new Observable(observer => {

            this.credentialService.lambda.invoke({
                FunctionName: environment.lostPasswordFunction,
                Payload: JSON.stringify({ 'email': email })
                }, (err, data) => {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        const output = typeof data.Payload === 'string'
                            ? JSON.parse(data.Payload)
                            : data.Payload;

                        observer.next(output);
                    }
                }
            );
        });
    }

    refreshCredentials(): void {
        console.log(`Attempting to refresh login credentials`);
        this.credentialService.creds.refresh(err => {
            if (!err) {
                console.log(`Successfully refreshed user credentials`);
            } else {
                console.log(`Could not refresh user credentials, logging out`);
                console.log(err);
                this.logout();
            }
        });
    }

    register(user){
        return new Observable(observer => {

            this.credentialService.lambda.invoke({
                FunctionName: environment.registerFunction,
                Payload: JSON.stringify(user)
                }, function(err, data) {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        let output = null;

                        if (typeof data.Payload === 'string') {
                            output = JSON.parse(data.Payload);
                        } else {
                            output = data.Payload;
                        }

                        if(output.created){
                            observer.next(output);
                        }else{
                            observer.error(output.message || output);
                        }
                    }
                }
            );
        });
    }

    adminRegister(user){
        return new Observable(observer => {

            this.credentialService.lambda.invoke({
                FunctionName: environment.adminRegisterFunction,
                Payload: JSON.stringify(user)
                }, function(err, data) {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        let output = null;

                        if (typeof data.Payload === 'string') {
                            output = JSON.parse(data.Payload);
                        } else {
                            output = data.Payload;
                        }

                        observer.next(output);
                    }
                }
            );
        });
    }

    resetPassword(email: string, lost: string, password: string) {
        return new Observable(observer => {

            const request = { 'email': email, 'lost': lost, 'password': password };

            this.credentialService.lambda.invoke({
                FunctionName: environment.resetPasswordFunction,
                Payload: JSON.stringify(request)
                }, (err, data) => {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        const output = typeof data.Payload === 'string'
                            ? JSON.parse(data.Payload)
                            : data.Payload;

                        observer.next(output);
                    }
                }
            );
        });
    }

    adminResetPassword(user: UserLoginForm, userEmail: string) {
        return new Observable(observer => {
            const request = { 'adminEmail': user.username, 'adminPassword': user.password, 'userEmail': userEmail };

            this.credentialService.lambda.invoke({
                FunctionName: environment.adminResetPasswordFunction,
                Payload: JSON.stringify(request)
                }, (err, data) => {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        const output = typeof data.Payload === 'string'
                            ? JSON.parse(data.Payload)
                            : data.Payload;

                        observer.next(output);
                    }
                }
            );
        });
    }

    updateUser(user: User): void {
        console.log('updating user');
        console.log(user);

        const variables = { user };
        const query = `mutation ($user: UserInput!) {
            updateUser(user: $user) {
                ${allUserFields}
            }
        }`;

        console.log({ query, variables });

        this.invokeUsersSettingMutation({ query, variables });
    }

    userIsAdmin() {
        const currentUser = localStorage.getItem('currentUser');
        if (!currentUser) {
            return false;
        }
        else {
            const role = typeof currentUser === 'string' && JSON.parse(currentUser)['role'];
            return role && role === 'ADMIN';
        }
    }

    userIsSupervisor() {
        const currentUser = localStorage.getItem('currentUser');
        if (!currentUser) {
            return false;
        }
        else {
            const role = typeof currentUser === 'string' && JSON.parse(currentUser)['role'];
            return role && role === 'SUPERVISOR';
        }
    }

    verify(email: string, verify: string, isAdmin: boolean){

        return new Observable(observer => {
            const request = {'email': email, 'verify': verify, 'isAdmin': isAdmin};
            this.credentialService.lambda.invoke({
                FunctionName: environment.verifyFunction,
                Payload: JSON.stringify(request)
                }, function(err, data) {
                    if (err) {
                        console.log(err, err.stack);
                        observer.error(err.stack);
                    } else {
                        let output = null;
                        if (typeof data.Payload === 'string'){
                            output = JSON.parse(data.Payload);
                        } else {
                            output = data.Payload;
                        }
                        if (output.verified){
                            observer.next(output);
                        } else {
                            observer.error(output.error);
                        }
                    }
            });
        });
    }

    private clearHorizonConfig(): void {
        try {
            if (typeof Storage !== 'undefined' && localStorage) {
                localStorage.removeItem('horizonConfig');
            }
        } catch (error) {
            console.warn('Failed to clear horizon config from localStorage:', error);
        }
    }
}


// WEBPACK FOOTER //
// /Users/jlam2/impresocloud/www/client/src/app/services/authentication.service.ts